import { motion, useAnimationControls } from 'framer-motion';
import { useEffect, useContext, useState, useCallback, useMemo } from 'react';
import { SIDENAV_MENU_ITEMS } from '../../constants';
import MenuItem from './MenuItem';
import {
  SidebarNavigationContext,
  useAuth,
  useNotification,
} from '../../contexts';
import { SystemRoles, ClientCode } from '../../enums/common';
import { ISideNavItem } from '../../types';
import { Modal, Spin } from 'antd';
import { QRCodeCanvas } from 'qrcode.react';
import { useLazyGetMobileApplicationLinkQuery } from '../../store';
import { cn, extractErrorMessageFromAPI } from '../../utils';
import { DownloadIcon, LibraryBig } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import denLogo from '../../assets/images/den_logo_image.svg';
import hathLogo from '../../assets/images/hathway_logo.png';
import softgelLogo from '../../assets/images/softgel_logo.png';
import sapphireLogo from '../../assets/images/sapphire_logo.png';

const containerVariants = {
  close: {
    width: '3rem',
    transition: { duration: 0.15, ease: 'easeOut' },
  },
  open: {
    width: '13rem',
    transition: { duration: 0.15, ease: 'easeOut' },
  },
} as const;

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const { isOpen, setIsOpen } = useContext(SidebarNavigationContext);
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileAppModalVisible, setMobileAppModalVisible] = useState(false);
  const [mobileAppDownloadLink, setMobileAppDownloadLink] = useState<
    string | null
  >(null);
  const [getMobileAppLink, mobileAppLinkResponse] =
    useLazyGetMobileApplicationLinkQuery();
  const containerControls = useAnimationControls();
  const showKnowledgeHub =
    import.meta.env.VITE_KNOWDLEGE_HUB_BUTTON?.toLowerCase() === 'true';

  // Memoize user roles extraction
  const assignedRoles = useMemo(() => {
    if (!user?.userPanelRoles?.length) return [];
    return user.userPanelRoles.map(item => item?.role?.name?.toUpperCase() as SystemRoles);
  }, [user?.userPanelRoles]);

  // Memoize menu filtering based on user roles
  const menu = useMemo<ISideNavItem[]>(() => {
    if (assignedRoles.length === 0) return [];

    return SIDENAV_MENU_ITEMS.reduce<ISideNavItem[]>((acc, menuItem) => {
      const allowedRoles = menuItem.allowedRoles || [];
      const hasAccess = assignedRoles.some(role => allowedRoles.includes(role));

      if (!hasAccess) return acc;

      if (menuItem.subMenuItems?.length) {
        const filteredSubItems = menuItem.subMenuItems.filter(subItem =>
          assignedRoles.some(role => subItem.allowedRoles?.includes(role)),
        );
        if (filteredSubItems.length > 0) {
          acc.push({ ...menuItem, subMenuItems: filteredSubItems });
        }
      } else {
        acc.push(menuItem);
      }

      return acc;
    }, []);
  }, [assignedRoles]);

  const filteredMenu = useMemo<ISideNavItem[]>(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return menu;

    return menu.reduce<ISideNavItem[]>((acc, item) => {
      const parentTitle = item.title?.toLowerCase() || '';
      const parentMatch = parentTitle.includes(query);

      if (item.subMenuItems?.length) {
        const filteredSubItems = item.subMenuItems.filter(subItem =>
          (subItem.title || '').toLowerCase().includes(query),
        );

        if (parentMatch || filteredSubItems.length > 0) {
          acc.push({
            ...item,
            subMenuItems: parentMatch ? item.subMenuItems : filteredSubItems,
          });
        }
      } else if (parentMatch) {
        acc.push(item);
      }

      return acc;
    }, []);
  }, [menu, searchTerm]);

  // Memoize logo source
  const logoSrc = useMemo(() => {
    const clientCode = user?.companyGroup?.clientCode;
    if (clientCode === ClientCode.DEN) return denLogo;
    if (clientCode === ClientCode.HATHWAY) return hathLogo;
    if (clientCode === ClientCode.SOFTGEL) return softgelLogo;
    if (clientCode === ClientCode.SAPPHIRE || clientCode === 'SAPH') return sapphireLogo;
    return null;
  }, [user?.companyGroup?.clientCode]);

  // Handlers
  const handleMouseEnter = useCallback(() => setIsOpen(true), [setIsOpen]);
  const handleMouseLeave = useCallback(() => setIsOpen(false), [setIsOpen]);
  const handleModalClose = useCallback(
    () => setMobileAppModalVisible(false),
    [],
  );

  const handleDownloadMobileAppClick = useCallback(async () => {
    try {
      const fileName = import.meta.env.VITE_MOBILE_APP_FILE_NAME;
      const response = await getMobileAppLink({ key: fileName }).unwrap();
      setMobileAppDownloadLink(response.data);
      setMobileAppModalVisible(true);
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) ||
        'Error in downloading mobile app link.',
      );
    }
  }, [getMobileAppLink, showNotification]);

  // Animation control effect
  useEffect(() => {
    containerControls.start(isOpen ? 'open' : 'close');
  }, [isOpen, containerControls]);

  return (
    <>
      <aside
        className="hidden md:block print:hidden"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <motion.nav
          variants={containerVariants}
          animate={containerControls}
          initial="close"
          className="bg-slate-900 flex flex-col z-30 fixed top-0 left-0 h-screen"
          aria-label="Main navigation"
        >
          {/* Logo Area */}
          <div className="h-12 flex items-center justify-center px-2 border-b border-slate-800">
            {logoSrc && (
             <img
             src={logoSrc}
             alt="Company Logo"
             className="w-[72px] h-auto object-contain"
           />
            )}
          </div>

          {/* Menu Items */}
          <nav
            className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2"
            aria-label="Sidebar menu"
          >
            {isOpen && (
              <div className="mb-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search menu..."
                  className="w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-400 outline-none focus:border-slate-500"
                />
              </div>
            )}
            <ul
              className={cn('flex flex-col', isOpen ? 'gap-0.5' : 'gap-1')}
              role="menu"
            >
              {filteredMenu.map((item, idx) => (
                <li key={item.title || idx} role="none">
                  <MenuItem item={item} />
                </li>
              ))}
              {isOpen && filteredMenu.length === 0 && (
                <li className="px-2 py-1.5 text-[11px] text-slate-400">
                  No menu found
                </li>
              )}
            </ul>
          </nav>

          {/* Knowledge Hub and Download App actions */}
          <div className="p-2 border-t border-slate-800 space-y-1">
            {showKnowledgeHub && <button
              type="button"
              onClick={() => navigate('/knowledge-hub')}
              className={cn(
                'w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors',
                { 'justify-center': !isOpen },
              )}
              title="Knowledge Hub"
              aria-label="Open Knowledge Hub"
            >
              <LibraryBig className="size-4 flex-shrink-0" aria-hidden="true" />
              {isOpen && <span className="truncate">Knowledge Hub</span>}
            </button>}
            <button
              type="button"
              onClick={handleDownloadMobileAppClick}
              className={cn(
                'w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors',
                { 'justify-center': !isOpen },
              )}
              title="Download App"
              aria-label="Download App"
            >
              <DownloadIcon
                className="size-4 flex-shrink-0"
                aria-hidden="true"
              />
              {isOpen && <span className="truncate">Download App</span>}
            </button>
          </div>
        </motion.nav>
      </aside>

      {/* QR Code Modal */}
      <Modal
        open={mobileAppModalVisible}
        footer={null}
        title={
          <div className="text-text-300 text-lg font-bold">
            Scan the QR to download mobile app                                          
          </div>
        }
        centered
        onCancel={handleModalClose}
        maskClosable={false}
        destroyOnClose
      >
        <div className="flex flex-col items-center justify-center space-y-4 py-12">
          {mobileAppLinkResponse.isLoading ? (
            <Spin />
          ) : (
            <QRCodeCanvas value={mobileAppDownloadLink || ''} size={200} />
          )}
        </div>
      </Modal>
    </>
  );
};

export default Sidebar;
